import { reportDraftDb } from "../report-draft/reportDraftDb";

// Registo do ciclo de vida de um turno offline (BEL-1751). PORTÁVEL: sem
// window/DOM obrigatórios, corre igual em página e Service Worker.
//
// Existe porque cada relato de "as fotos desapareceram" obrigava a arqueologia
// manual em produção e, mesmo assim, ficava por responder quando a foto subiu:
// o Last-Modified do S3 é reescrito pelo setVisibility do storeTurn, o Sentry
// não tem tracing ligado, e device_meta chegava sempre null.
//
// Os eventos ficam no próprio registo do turno no IndexedDB e sobem no POST do
// turno, dentro de device_meta — um campo que JÁ viaja no payload e JÁ é
// gravado. Zero pedidos de rede novos: a telemetria só viaja quando o
// dispositivo já tem ligação e já ia falar com o servidor de qualquer forma.

/**
 * Teto de eventos por turno. Um turno de 15 fotos com retentativas de PUT
 * (até 5 por foto) chega perto disto; acima é ruído. Cortam-se os mais
 * ANTIGOS — o que interessa é como o turno acabou.
 */
export const MAX_TURN_EVENTS = 60;

let drainTrigger = null;

/**
 * Eventos capturados ANTES de o turno existir no IndexedDB.
 *
 * `capturePhoto` corre por foto, antes de `closeTurnWithUuid` gravar o
 * manifesto — nessa altura não há registo onde escrever. Ficam aqui, em
 * memória, e o fecho do turno esvazia-os para dentro do registo. Um turno
 * abandonado (o utilizador sai sem fechar) deixa no máximo MAX_TURN_EVENTS
 * entradas, que morrem com a página.
 *
 * @type {Map<string, Array<object>>}
 */
const buffered = new Map();

/**
 * Retira e devolve os eventos guardados para um turno. Destrutivo: chamado uma
 * única vez, por `closeTurnWithUuid`.
 *
 * @param {string} turnUuid
 * @returns {Array<object>}
 */
export function flushBufferedEvents(turnUuid) {
	const events = buffered.get(turnUuid) || [];
	buffered.delete(turnUuid);

	return events;
}

/** Marca o que motivou o drain em curso (load, online, focus, foreground, timer). */
export function setDrainTrigger(trigger) {
	drainTrigger = trigger || null;
}

export function currentDrainTrigger() {
	return drainTrigger;
}

/**
 * Estado da ligação, defensivo. `navigator.connection` não existe em node
 * (ambiente de teste) nem em Safari/iOS; `navigator` pode nem existir no SW
 * de alguns ambientes.
 *
 * @returns {{onLine: boolean|null, effectiveType: string|null, downlink: number|null}}
 */
export function connectionSnapshot() {
	if (typeof navigator === "undefined") {
		return { onLine: null, effectiveType: null, downlink: null };
	}
	const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
	return {
		onLine: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
		effectiveType: c && c.effectiveType ? c.effectiveType : null,
		downlink: c && typeof c.downlink === "number" ? c.downlink : null,
	};
}

/**
 * Acrescenta um evento ao turno. Best-effort: um turno já apagado ou um erro
 * do IndexedDB nunca podem partir o upload — a telemetria é secundária ao
 * trabalho do GPV.
 *
 * @param {string} turnUuid
 * @param {string} event
 * @param {object} [data]
 */
export async function appendTurnEvent(turnUuid, event, data = {}) {
	if (!turnUuid || !event) {
		return;
	}
	const entry = { t: new Date().toISOString(), e: event, ...data };
	try {
		const turn = await reportDraftDb.getTurn(turnUuid);
		if (!turn) {
			// Ainda não há manifesto (capturePhoto antes de closeTurnWithUuid) —
			// guarda em memória até o fecho do turno o esvaziar.
			const pendentes = buffered.get(turnUuid) || [];
			pendentes.push(entry);
			buffered.set(turnUuid, pendentes.length > MAX_TURN_EVENTS ? pendentes.slice(-MAX_TURN_EVENTS) : pendentes);

			return;
		}
		const events = Array.isArray(turn.events) ? turn.events : [];
		events.push(entry);
		turn.events = events.length > MAX_TURN_EVENTS ? events.slice(-MAX_TURN_EVENTS) : events;
		await reportDraftDb.putTurn(turn);
	} catch (e) {
		// best-effort — nunca propaga
	}
}

/** Chave kv onde ficam os cancelamentos até subirem. */
const CANCELLED_KEY = "assistant_cancelled_turns";

/** Teto de cancelamentos retidos. Acima disto o sinal já é óbvio. */
const MAX_CANCELLED = 20;

/**
 * Regista que o utilizador cancelou uma captura ainda local.
 *
 * `turnKernel.deleteTurn` apaga fotos, ops e manifesto — os eventos do turno
 * morreriam com ele. Sem este registo, uma foto removida de propósito fica
 * indistinguível de uma foto perdida por bug (BEL-1751). Vive num kv à parte e
 * sobe na boleia do próximo turno confirmado da mesma visita.
 *
 * @param {{turnUuid: string, visitId: string|number, photoUuids?: Array<string>, capturedAt?: string}} turn
 */
export async function recordCancelledTurn(turn) {
	if (!turn || !turn.turnUuid) {
		return;
	}
	try {
		const atuais = (await reportDraftDb.getKv(CANCELLED_KEY)) || [];
		const lista = Array.isArray(atuais) ? atuais : [];
		lista.push({
			uuid: turn.turnUuid,
			visitId: String(turn.visitId),
			photos: Array.isArray(turn.photoUuids) ? turn.photoUuids.length : 0,
			capturedAt: turn.capturedAt || null,
			at: new Date().toISOString(),
		});
		await reportDraftDb.putKv(CANCELLED_KEY, lista.slice(-MAX_CANCELLED));
	} catch (e) {
		// best-effort — nunca propaga
	}
}

/**
 * Lê os cancelamentos de uma visita para subirem no payload do turno.
 *
 * NÃO destrutivo, de propósito: o POST do turno pode falhar e ficar na fila
 * para retentar. Consumir aqui perdia os cancelamentos para sempre — uma perda
 * silenciosa dentro da funcionalidade criada precisamente para acabar com
 * perdas silenciosas. Quem limpa é `clearCancelledTurns`, e só depois de o POST
 * passar.
 *
 * @param {string|number} visitId
 * @returns {Promise<Array<{uuid: string, visitId: string, photos: number, ageMs: number|null, at: string}>>}
 */
export async function readCancelledTurns(visitId) {
	try {
		const atuais = (await reportDraftDb.getKv(CANCELLED_KEY)) || [];
		const lista = Array.isArray(atuais) ? atuais : [];
		const alvo = String(visitId);
		const agora = Date.now();

		return lista
			.filter(c => c && c.visitId === alvo)
			.map(c => {
				const capturado = Date.parse(c.capturedAt);

				return {
					uuid: c.uuid,
					visitId: c.visitId,
					photos: c.photos,
					ageMs: Number.isFinite(capturado) ? agora - capturado : null,
					at: c.at,
				};
			});
	} catch (e) {
		return [];
	}
}

/**
 * Descarta os cancelamentos de uma visita, depois de terem subido. Idempotente:
 * chamar duas vezes não parte nada.
 *
 * @param {string|number} visitId
 */
export async function clearCancelledTurns(visitId) {
	try {
		const atuais = (await reportDraftDb.getKv(CANCELLED_KEY)) || [];
		const lista = Array.isArray(atuais) ? atuais : [];
		const alvo = String(visitId);

		await reportDraftDb.putKv(
			CANCELLED_KEY,
			lista.filter(c => !c || c.visitId !== alvo)
		);
	} catch (e) {
		// best-effort — nunca propaga
	}
}

/** Kv key for the trace of turn ops discarded because their record vanished. */
const DROPPED_KEY = "assistant_dropped_turn_ops";

/** Cap of retained drops. Beyond this the signal is already obvious. */
const MAX_DROPPED = 20;

/**
 * Records the discard of a turn op whose manifest no longer exists.
 *
 * This was the silent final step of the BEL-1751 race: the record died in
 * another execution and the drain just removed the op from the queue — the
 * loss left not a single byte of trace. Rides along the next confirmed turn
 * POST of the same visit, like the cancellations.
 */
export async function recordDroppedTurnOp(visitId, turnUuid) {
	if (!turnUuid) {
		return;
	}
	try {
		// A cancellation is not a loss — recording it here would send the next
		// investigation chasing a ghost. A cancel (deleteTurn) racing an
		// in-flight drain leaves the op orphaned in the queue (the record is
		// already gone), but the turn died by the operator's decision, not by
		// a bug — it must not count as "dropped".
		const cancelled = (await reportDraftDb.getKv(CANCELLED_KEY)) || [];
		const cancelledList = Array.isArray(cancelled) ? cancelled : [];
		if (cancelledList.some(c => c && c.uuid === turnUuid)) {
			return;
		}
		const current = (await reportDraftDb.getKv(DROPPED_KEY)) || [];
		const list = Array.isArray(current) ? current : [];
		list.push({ uuid: turnUuid, visitId: String(visitId), at: new Date().toISOString() });
		await reportDraftDb.putKv(DROPPED_KEY, list.slice(-MAX_DROPPED));
	} catch (e) {
		// best-effort — never propagates
	}
}

/**
 * Reads a visit's dropped ops so they ride along in the turn payload.
 * NON-destructive: the POST may fail and stay queued — cleanup belongs to
 * `clearDroppedTurnOps`, only after the POST succeeds.
 */
export async function readDroppedTurnOps(visitId) {
	try {
		const current = (await reportDraftDb.getKv(DROPPED_KEY)) || [];
		return (Array.isArray(current) ? current : []).filter(d => d && d.visitId === String(visitId));
	} catch (e) {
		return [];
	}
}

/** Clears a visit's dropped ops — call ONLY after a successful POST. */
export async function clearDroppedTurnOps(visitId) {
	try {
		const current = (await reportDraftDb.getKv(DROPPED_KEY)) || [];
		const rest = (Array.isArray(current) ? current : []).filter(d => d && d.visitId !== String(visitId));
		await reportDraftDb.putKv(DROPPED_KEY, rest);
	} catch (e) {
		// best-effort — never propagates
	}
}
